import { PropsWithChildren } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Navigate } from 'react-router';
import AuthProtectedRoute from './AuthProtectedRoute';
import CasinoAccessGuard from './CasinoAccessGuard';
import MaintenanceProtectedRoute, { MaintenanceProduct } from './MaintenanceProtectedRoute';
import SkinFeatureGuard from './SkinFeatureGuard';
import SportsbookAccessGuard from './SportsbookAccessGuard';
import { MiniStatsProvider } from '../contexts/MiniStatsWidgetContext';
import { SkinFeatureKey } from '../config/skinRegistry/configs';
import type { FeatureFlagKey } from '../constants/featureFlags';

type Props = {
  auth?: boolean;
  maintenance?: boolean;
  maintenanceProduct?: MaintenanceProduct;
  miniStats?: boolean;
  requiredFeature?: SkinFeatureKey;
  requiredGbFeature?: FeatureFlagKey;
  sportsbookAccess?: boolean;
  casinoAccess?: boolean;
};

const GrowthBookFeatureGuard = ({ feature, children }: PropsWithChildren<{ feature: FeatureFlagKey }>) => {
  const isEnabled = useFeatureIsOn(feature);

  if (!isEnabled) {
    return <Navigate to='/' replace />;
  }

  return <>{children}</>;
};

const RouteGuardWrapper = ({
  auth = false,
  maintenance = true,
  maintenanceProduct = 'sportsbook',
  miniStats = false,
  requiredFeature,
  requiredGbFeature,
  sportsbookAccess = false,
  casinoAccess = false,
  children,
}: PropsWithChildren<Props>) => {
  let wrapped = children;
  const rootPath = '/';

  if (sportsbookAccess) {
    wrapped = <SportsbookAccessGuard redirect={rootPath}>{wrapped}</SportsbookAccessGuard>;
  }

  if (casinoAccess) {
    wrapped = <CasinoAccessGuard redirect={rootPath}>{wrapped}</CasinoAccessGuard>;
  }

  if (requiredFeature) {
    wrapped = (
      <SkinFeatureGuard feature={requiredFeature} redirect={rootPath}>
        {wrapped}
      </SkinFeatureGuard>
    );
  }

  if (miniStats) {
    wrapped = <MiniStatsProvider>{wrapped}</MiniStatsProvider>;
  }

  if (maintenance) {
    wrapped = <MaintenanceProtectedRoute product={maintenanceProduct}>{wrapped}</MaintenanceProtectedRoute>;
  }

  if (auth) {
    wrapped = <AuthProtectedRoute>{wrapped}</AuthProtectedRoute>;
  }

  if (requiredGbFeature) {
    wrapped = <GrowthBookFeatureGuard feature={requiredGbFeature}>{wrapped}</GrowthBookFeatureGuard>;
  }

  return <>{wrapped}</>;
};

export default RouteGuardWrapper;
